import { Fill, SvgGraphics, Filter } from '@tencent/alloy-renderer';
import isEmpty from 'lodash/isEmpty';
import { DisplayRule, FillMode, generateEffectFilters, IFrame, setFillStyle } from '@tencent/alloy-graphics';
import {
    ShapeOriginStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import {
    getEffectFilter,
    getFillStyle,
    getStrokeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import {
    convertFrame,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/custom-render/utils/style';
import { IEffect, Stroke } from '@tencent/alloy-renderer-adapter';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
/**
 * 设置元素样式，包括填充、边框、特效等
 * @param element
 * @param orgStyle
 * @param factoryOptions
 */
export function setElementStyle(
    element: SvgGraphics,
    orgStyle: ShapeOriginStyle,
    factoryOptions?: InterfaceFactoryOptions,
) {
    // 设置背景样式
    setElementFill(element, orgStyle, factoryOptions);

    // 设置边框样式
    setElementStroke(element, orgStyle, factoryOptions);

    // 设置元素特效
    setElementFilter(element, orgStyle, factoryOptions);
}

/**
 * 设置元素填充效果
 * @param element
 * @param orgStyle 原始样式数据
 * @param factoryOptions
 */
export function setElementFill(
    element: SvgGraphics,
    orgStyle: ShapeOriginStyle,
    factoryOptions?: InterfaceFactoryOptions,
) {
    const { outline, background } = orgStyle;
    if (!isEmpty(background)) {
        const fillStyle = getFillStyle({ fillBackground: background }, factoryOptions, true);
        const frame: IFrame = convertFrame(element, outline?.width);
        if (fillStyle && frame.width > 0 && frame.height > 0) {  // 宽高都大于0才去设置填充
            setFillStyle(element, fillStyle, { frame, fillMode: FillMode.NORMAL });
        }
    }
}

/**
 * 设置元素边框效果
 * @param element
 * @param orgStyle
 * @param factoryOptions
 */
export function setElementStroke(
    element: SvgGraphics,
    orgStyle: ShapeOriginStyle,
    factoryOptions?: InterfaceFactoryOptions,
) {
    const { outline } = orgStyle;
    if (!isEmpty(outline)) {
        const strokeStyle = getStrokeStyle({ style: { border: outline }, factoryOptions });
        if (strokeStyle?.fillStyle?.gradFill) {  // 必须rotateWithShape设置为false，否则渐变效果不对
            strokeStyle.fillStyle.gradFill.rotateWithShape = false;
        }
        if (!strokeStyle) {
            element.stroke = Fill.None;
            element.strokeWidth = 0;
            element.hitStrokeWidth = 0;
            return;
        }
        const lineWidth = strokeStyle.lineWidth || 0;
        const stroke = new Stroke(
            strokeStyle,
            { scaleX: 1, scaleY: 1 }, convertFrame(element, lineWidth),
        );
        element.strokeWidth = stroke.strokeWidth;
        element.stroke = stroke.strokeFill;
        element.strokeLineCap = stroke.lineCap;
        element.strokeLineJoin = stroke.lineJoin;
        element.strokeDash = stroke.lineDash;
    }
}

const DefaultDisplayOptions = {
    shadow: DisplayRule.Both,
    glow: DisplayRule.Both,
    softEdge: DisplayRule.Both,
    reflection: DisplayRule.Both,
    image: DisplayRule.Both,
};

/**
 * 设置元素特效(阴影，发光等)
 * @param element
 * @param orgStyle
 * @param factoryOptions
 */
export function setElementFilter(
    element: SvgGraphics,
    orgStyle: ShapeOriginStyle,
    factoryOptions?: InterfaceFactoryOptions,
) {
    const { outline, background, effects } = orgStyle;
    if (isEmpty(effects)) return;
    if (isEmpty(outline) && isEmpty(background)) return;  // 无边框并且无背景，不显示特效
    const hasStroke = !isEmpty(outline) && (outline?.color || outline?.gradFill);
    // 图表中所有的形状在填充和边框都为无填充的情况下可以设置效果，此时填充为 noFill
    // 只有线条时（例如网格线、折线图、趋势线），线条为无填充时，没有effect。此时填充为undefined
    const hasFill = !isEmpty(background);
    if (!hasFill && !hasStroke) return; // 无边框并且背景为空，不显示特效

    const effectFilter: IEffect | undefined = getEffectFilter(effects);
    if (!effectFilter) return;
    const filters: Filter[] = generateEffectFilters(effectFilter, {
        forbidWebgl: FORBID_WEBGL_FILTER,
        isPicture: !!background?.image,
        filterDisplayOptions: DefaultDisplayOptions,
    });

    // 清空原来特效，并添加新特效
    element.filters = [];
    filters.forEach((filter) => {
        element.addFilter(filter);
    });
}
